import {
  vehicleManuallySelected,
  getNumberOfPolicyHolders,
  isPolicyIssuedToPerson,
  hasModifications,
  sameOvernightAndPostalAddress,
  isPostalAddressLockedBagGpo,
  isPostalAddressDifferentManual,
  getPolicyStartDate,
  isOneYearOldOrLessVehicle,
  isComprehensiveSelected,
  getPaymentDate,
  getPreselectedPaymentFrequency,
  isMainDriver,
} from 'scenes/motor/selectors';
import {
  MotorStepType,
  MotorFunnelState,
  MotorFlowType,
  MotorFlowDefinition,
  MotorFormAnswer,
} from 'scenes/motor/types';
import { defaultPaymentDetails } from 'scenes/common/flows/paymentDetails/defaults';
import { defaultPolicyHolder } from 'scenes/motor/flows/addPolicyHolder';
import { Driver } from '../addDriver/types';
import { toPolicyHolder } from '../coverOptions';

const setPolicyHoldersIfEmpty = (state: MotorFunnelState) => ({
  ...{
    policyHolders: [
      toPolicyHolder(state.quoteDetails, true),
      ...state[MotorFlowType.QuoteDetails].additionalDrivers.map((driver) =>
        toPolicyHolder(driver)
      ),
    ],
  },
});

const insuredSincePossessedOrSpecialDetails = (
  state: MotorFunnelState
): MotorStepType =>
  isOneYearOldOrLessVehicle(state)
    ? MotorStepType.InsuredSincePossessed
    : MotorStepType.SpecialDetails;

const afterAddressStep = (state: MotorFunnelState): MotorStepType => {
  if (isComprehensiveSelected(state)) {
    return vehicleManuallySelected(state)
      ? MotorStepType.MissingRegoStep
      : MotorStepType.PreExistingDamage;
  }
  return vehicleManuallySelected(state)
    ? MotorStepType.MissingRegoStep
    : insuredSincePossessedOrSpecialDetails(state);
};

const beforePreExistingDamage = (state: MotorFunnelState): MotorStepType => {
  if (vehicleManuallySelected(state)) {
    return MotorStepType.MissingRegoStep;
  }
  if (sameOvernightAndPostalAddress(state)) {
    return MotorStepType.PostalAddress;
  }
  if (isPostalAddressLockedBagGpo(state)) {
    return MotorStepType.LockedBagAddress;
  }
  if (isPostalAddressDifferentManual(state)) {
    return MotorStepType.DifferentPostalAddressManual;
  }
  return MotorStepType.DifferentPostalAddress;
};

const beforeSpecialDetails = (state: MotorFunnelState): MotorStepType => {
  if (isComprehensiveSelected(state)) {
    return isOneYearOldOrLessVehicle(state)
      ? MotorStepType.InsuredSincePossessed
      : MotorStepType.SpecialFeatures;
  }
  return isOneYearOldOrLessVehicle(state)
    ? MotorStepType.InsuredSincePossessed
    : beforePreExistingDamage(state);
};

const driverNameUpdate = (
  firstName: string,
  lastName: string,
  funnelState: MotorFunnelState
):
  | { firstName?: string; lastName?: string }
  | { additionalDrivers: Driver[] } => {
  const { driverIndex } = funnelState.quoteDetails;
  const mainDriver = isMainDriver(funnelState);

  if (mainDriver) {
    return {
      firstName,
      lastName,
    };
  }

  return {
    additionalDrivers: funnelState.quoteDetails.additionalDrivers.map(
      (driver, index) =>
        index === driverIndex ? { ...driver, firstName, lastName } : driver
    ),
  };
};

export const forwardLinks: MotorFlowDefinition = {
  [MotorStepType.addDriverName]: (
    funnelState: MotorFunnelState,
    answer: MotorFormAnswer
  ) =>
    answer.formValues.editingDriver
      ? {
          step: MotorStepType.Name,
        }
      : {
          step: MotorStepType.PersonalOrBusinessPolicy,
          [MotorFlowType.PolicyDetails]: {
            ...funnelState.policyDetails,
            ...setPolicyHoldersIfEmpty(funnelState),
            overnightAddress: funnelState.quoteDetails.overnightParkingAddress,
            preselectedPaymentFrequency: getPreselectedPaymentFrequency(
              funnelState
            ),
          },
        },
  [MotorStepType.Name]: (
    funnelState: MotorFunnelState,
    answer: MotorFormAnswer
  ) => ({
    step: MotorStepType.addDriverName,
    // 'Cross slice' update: update name
    // in quoteDetails
    [MotorFlowType.QuoteDetails]: {
      ...funnelState.quoteDetails,
      driverIndex: undefined,
      ...driverNameUpdate(
        answer.formValues.firstName ?? '',
        answer.formValues.lastName ?? '',
        funnelState
      ),
    },
  }),
  [MotorStepType.PersonalOrBusinessPolicy]: (
    funnelState: MotorFunnelState
  ) => ({
    step: isPolicyIssuedToPerson(funnelState)
      ? MotorStepType.AddPolicyHolder
      : MotorStepType.BusinessNameAndAbn,
  }),
  [MotorStepType.BusinessNameAndAbn]: {
    step: MotorStepType.BusinessRegisteredForGst,
  },
  [MotorStepType.BusinessRegisteredForGst]: {
    step: MotorStepType.BusinessContact,
  },
  [MotorStepType.BusinessContact]: {
    step: MotorStepType.AddPolicyHolder,
  },
  [MotorStepType.AddPolicyHolder]: (
    funnelState: MotorFunnelState,
    answer: MotorFormAnswer
  ) => {
    if (answer.formValues.addPolicyHolder) {
      return {
        step: MotorStepType.Name,
        flow: MotorFlowType.AddPolicyHolder,
        [MotorFlowType.QuoteDetails]: {
          ...funnelState.quoteDetails,
          driverIndex: getNumberOfPolicyHolders(funnelState),
        },
        [MotorFlowType.AddPolicyHolder]: {
          ...defaultPolicyHolder,
          index: getNumberOfPolicyHolders(funnelState),
        },
      };
    }
    if (isPolicyIssuedToPerson(funnelState)) {
      return { step: MotorStepType.PersonalPolicyContact };
    }
    return { step: MotorStepType.ContactPhoneNumber };
  },
  [MotorStepType.PersonalPolicyContact]: {
    step: MotorStepType.ContactPhoneNumber,
  },
  [MotorStepType.ContactPhoneNumber]: {
    step: MotorStepType.ContactEmailAddress,
  },
  [MotorStepType.ContactEmailAddress]: {
    step: MotorStepType.PostalAddress,
  },
  [MotorStepType.PostalAddress]: (funnelState: MotorFunnelState) => {
    if (sameOvernightAndPostalAddress(funnelState)) {
      return { step: afterAddressStep(funnelState) };
    }
    return {
      step: isPostalAddressLockedBagGpo(funnelState)
        ? MotorStepType.LockedBagAddress
        : MotorStepType.DifferentPostalAddress,
    };
  },
  [MotorStepType.DifferentPostalAddress]: (funnelState: MotorFunnelState) => ({
    step: isPostalAddressDifferentManual(funnelState)
      ? MotorStepType.DifferentPostalAddressManual
      : afterAddressStep(funnelState),
  }),
  [MotorStepType.DifferentPostalAddressManual]: (
    funnelState: MotorFunnelState
  ) => ({ step: afterAddressStep(funnelState) }),
  [MotorStepType.LockedBagAddress]: (funnelState: MotorFunnelState) => ({
    step: afterAddressStep(funnelState),
  }),
  [MotorStepType.MissingRegoStep]: (funnelState: MotorFunnelState) => ({
    step: isComprehensiveSelected(funnelState)
      ? MotorStepType.PreExistingDamage
      : insuredSincePossessedOrSpecialDetails(funnelState),
  }),
  [MotorStepType.PreExistingDamage]: {
    step: MotorStepType.Modifications,
  },
  [MotorStepType.Modifications]: (funnelState: MotorFunnelState) => ({
    step: hasModifications(funnelState)
      ? MotorStepType.ModificationSelection
      : MotorStepType.SpecialFeatures,
  }),
  [MotorStepType.ModificationSelection]: {
    step: MotorStepType.SpecialFeatures,
  },
  [MotorStepType.SpecialFeatures]: (funnelState: MotorFunnelState) => ({
    step: isOneYearOldOrLessVehicle(funnelState)
      ? MotorStepType.InsuredSincePossessed
      : MotorStepType.SpecialDetails,
  }),
  [MotorStepType.InsuredSincePossessed]: {
    step: MotorStepType.SpecialDetails,
  },
  [MotorStepType.SpecialDetails]: {
    step: MotorStepType.TermsAndConditions,
  },
  [MotorStepType.TermsAndConditions]: { step: MotorStepType.Summary },
  [MotorStepType.Summary]: (funnelState: MotorFunnelState) => ({
    step: MotorStepType.PaymentFrequency,
    flow: MotorFlowType.PaymentDetails,
    [MotorFlowType.PaymentDetails]: {
      ...defaultPaymentDetails,
      paymentFrequency: getPreselectedPaymentFrequency(funnelState),
      paymentDate:
        getPaymentDate(funnelState) || getPolicyStartDate(funnelState),
    },
  }),
};

export const backwardLinks: MotorFlowDefinition = {
  [MotorStepType.addDriverName]: {
    step: MotorStepType.OptionalExtras,
    flow: MotorFlowType.CoverOptions,
  },
  [MotorStepType.Name]: {
    step: MotorStepType.addDriverName,
  },
  [MotorStepType.PersonalOrBusinessPolicy]: (
    funnelState: MotorFunnelState
  ) => ({
    step: MotorStepType.addDriverName,
    flow: MotorFlowType.PolicyDetails,
    coverOptions: {
      ...funnelState.coverOptions,
      preselectedPaymentFrequency:
        funnelState.policyDetails.preselectedPaymentFrequency,
    },
  }),
  [MotorStepType.BusinessNameAndAbn]: {
    step: MotorStepType.PersonalOrBusinessPolicy,
  },
  [MotorStepType.BusinessRegisteredForGst]: {
    step: MotorStepType.BusinessNameAndAbn,
  },
  [MotorStepType.BusinessContact]: {
    step: MotorStepType.BusinessRegisteredForGst,
  },
  [MotorStepType.AddPolicyHolder]: (funnelState: MotorFunnelState) => ({
    step: isPolicyIssuedToPerson(funnelState)
      ? MotorStepType.PersonalOrBusinessPolicy
      : MotorStepType.BusinessContact,
  }),
  [MotorStepType.PersonalPolicyContact]: {
    step: MotorStepType.AddPolicyHolder,
  },
  [MotorStepType.ContactPhoneNumber]: (funnelState: MotorFunnelState) => ({
    step: isPolicyIssuedToPerson(funnelState)
      ? MotorStepType.PersonalPolicyContact
      : MotorStepType.AddPolicyHolder,
  }),
  [MotorStepType.ContactEmailAddress]: {
    step: MotorStepType.ContactPhoneNumber,
  },
  [MotorStepType.PostalAddress]: {
    step: MotorStepType.ContactEmailAddress,
  },
  [MotorStepType.DifferentPostalAddress]: {
    step: MotorStepType.PostalAddress,
  },
  [MotorStepType.DifferentPostalAddressManual]: {
    step: MotorStepType.DifferentPostalAddress,
  },
  [MotorStepType.LockedBagAddress]: {
    step: MotorStepType.PostalAddress,
  },
  [MotorStepType.MissingRegoStep]: (funnelState: MotorFunnelState) => {
    if (sameOvernightAndPostalAddress(funnelState)) {
      return { step: MotorStepType.PostalAddress };
    }
    if (isPostalAddressLockedBagGpo(funnelState)) {
      return { step: MotorStepType.LockedBagAddress };
    }
    if (isPostalAddressDifferentManual(funnelState)) {
      return { step: MotorStepType.DifferentPostalAddressManual };
    }
    return { step: MotorStepType.DifferentPostalAddress };
  },
  [MotorStepType.PreExistingDamage]: (funnelState: MotorFunnelState) => ({
    step: beforePreExistingDamage(funnelState),
  }),
  [MotorStepType.Modifications]: {
    step: MotorStepType.PreExistingDamage,
  },
  [MotorStepType.ModificationSelection]: {
    step: MotorStepType.Modifications,
  },
  [MotorStepType.SpecialFeatures]: (funnelState: MotorFunnelState) => ({
    step: hasModifications(funnelState)
      ? MotorStepType.ModificationSelection
      : MotorStepType.Modifications,
  }),
  [MotorStepType.InsuredSincePossessed]: (funnelState: MotorFunnelState) => ({
    step: isComprehensiveSelected(funnelState)
      ? MotorStepType.SpecialFeatures
      : beforePreExistingDamage(funnelState),
  }),
  [MotorStepType.SpecialDetails]: (funnelState: MotorFunnelState) => ({
    step: beforeSpecialDetails(funnelState),
  }),
  [MotorStepType.TermsAndConditions]: {
    step: MotorStepType.SpecialDetails,
  },
  [MotorStepType.Summary]: { step: MotorStepType.TermsAndConditions },
};

export const editPolicyDetails = (
  funnelState: MotorFunnelState,
  action: MotorFormAnswer
) => ({
  step: action.targetStep,
  flow: MotorFlowType.PolicyDetails,
});
